import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Text,
  Heading,
  Stack,
  Grid,
  GridItem,
  Button,
  VStack,
} from '@chakra-ui/react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
);

const TokenomicsCharts = () => {
  const pieData = {
    labels: [
      'Lottery Payouts', 
      'Lottery Rebate',
      'Pool Maintenance',
      'Pool Fund'
    ],
    datasets: [
      {
        data: [70, 10, 2, 18],
        backgroundColor: [
          '#4299E1',
          '#48BB78',
          '#9F7AEA',
          '#F6AD55'
        ],
        borderColor: [
          '#2B6CB0',
          '#2F855A',
          '#6B46C1',
          '#C05621'
        ],
        borderWidth: 1,
      },
    ],
  };

  const tokenGrowthData = {
    labels: ['Launch', 'Early', 'Growth', 'Mature'],
    datasets: [
      {
        label: 'Token Value',
        data: [1.0, 1.3, 1.6, 2.0],
        borderColor: '#48BB78',
        backgroundColor: 'rgba(72, 187, 120, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          boxWidth: 10,
          padding: 10,
          color: '#CBD5E0',
          font: {
            size: 8,
            family: 'system-ui'
          }
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 10
        },
        bodyFont: {
          size: 10
        },
        padding: 6,
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw}%`;
          }
        }
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 10
        },
        bodyFont: {
          size: 10
        },
        padding: 6
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#CBD5E0',
          callback: function(value) {
            return value + 'x';
          },
          font: {
            size: 8
          },
          maxTicksLimit: 5
        },
        title: {
          display: false
        }
      },
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#CBD5E0',
          font: {
            size: 8
          }
        }
      },
    },
  };

  return (
    <Grid 
      templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
      gap={{ base: 4, md: 6 }}
      w="100%"
    >
      <GridItem>
        <Box 
          p={{ base: 3, md: 4 }} 
          bg="gray.800" 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="blue.500"
          h="100%"
        >
          <Heading 
            as="h4" 
            size={{ base: "sm", md: "md" }} 
            color="white" 
            mb={3}
            textAlign="center"
          >
            Fund Distribution
          </Heading>
          <Box 
            height={{ base: "200px", sm: "250px", md: "300px" }} 
            width="100%"
            position="relative"
            px={{ base: 0, md: 4 }}
          >
            <Pie data={pieData} options={options} />
          </Box>
        </Box>
      </GridItem>

      <GridItem>
        <Box 
          p={{ base: 3, md: 4 }} 
          bg="gray.800" 
          borderRadius="lg" 
          borderWidth="1px" 
          borderColor="blue.500"
          h="100%"
        >
          <Heading 
            as="h4" 
            size={{ base: "sm", md: "md" }} 
            color="white" 
            mb={3}
            textAlign="center"
          >
            Token Value Appreciation
          </Heading>
          <Box 
            height={{ base: "200px", sm: "250px", md: "300px" }}
            width="100%"
            position="relative"
            px={{ base: 0, md: 4 }}
          >
            <Line data={tokenGrowthData} options={lineOptions} />
          </Box>
        </Box>
      </GridItem>
    </Grid>
  );
};

const DocPage = () => {
  const navigate = useNavigate();
  
  return (
    <Box bg="gray.900" minH="100vh" w="100%">
      <Container 
        maxW={{ base: "100%", md: "container.lg" }} 
        p={0}
      >
        <VStack spacing={{ base: 3, md: 6 }} align="stretch" w="100%">
          <Box textAlign="center" mb={{ base: 3, md: 8 }} px={2}>
            <Heading 
              as="h1" 
              size={{ base: "lg", md: "2xl" }}
              color="white" 
              mb={{ base: 2, md: 4 }}
              cursor="pointer"
              _hover={{ color: 'blue.400' }}
              onClick={() => navigate('/')}
            >
              BitBitWin Documentation
            </Heading>
            <Text 
              fontSize={{ base: "sm", md: "xl" }} 
              color="gray.400" 
              mb={{ base: 3, md: 6 }}
            >
              A Revolutionary DeFi Instant Lottery Platform
            </Text>
            <Button
              colorScheme="blue"
              size={{ base: "sm", md: "lg" }}
              onClick={() => navigate('/app')}
              mb={{ base: 2, md: 4 }}
              px={8}
            >
              Launch App
            </Button>
          </Box>

          <Box px={{ base: 2, md: 0 }}>
            <VStack spacing={{ base: 3, md: 6 }} w="100%">
              <Box 
                p={{ base: 4, md: 6 }} 
                bg="gray.800" 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor="blue.500"
                w="100%"
              >
                <Heading 
                  as="h2" 
                  size={{ base: "md", md: "lg" }} 
                  color="white" 
                  mb={{ base: 2, md: 4 }}
                >
                  Overview
                </Heading>
                <Text 
                  color="gray.400"
                  fontSize={{ base: "xs", md: "md" }}
                  lineHeight="tall"
                  w="100%"
                >
                  BitBitWin is a decentralized lottery platform that combines traditional lottery excitement
                  with DeFi mechanics, offering transparent, fair, and rewarding gaming experiences.
                </Text>
              </Box>

              <Box 
                p={{ base: 4, md: 6 }} 
                bg="gray.800" 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor="blue.500"
                w="100%"
              >
                <Heading 
                  as="h2" 
                  size={{ base: "md", md: "lg" }} 
                  color="white" 
                  mb={{ base: 2, md: 4 }}
                >
                  Key Features
                </Heading>
                <VStack align="stretch" spacing={{ base: 3, md: 4 }} w="100%">
                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      1. High Return Rate
                    </Heading>
                    <Text 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      lineHeight="tall"
                      w="100%"
                    >
                      With a 70% expected return rate plus pool token rebates, BitBitWin offers significantly higher
                      returns compared to traditional lotteries which typically return less than 40%.
                    </Text>
                  </Box>
                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      2. Multi-Asset Support
                    </Heading>
                    <Text 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      lineHeight="tall"
                      w="100%"
                    >
                      Choose from various lottery pools with different underlying assets. Whether you prefer ETH, 
                      stablecoins, or other cryptocurrencies, BitBitWin provides multiple options to suit your 
                      preferences.
                    </Text>
                  </Box>
                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      3. Instant Results
                    </Heading>
                    <Text 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      lineHeight="tall"
                      w="100%"
                    >
                      Unlike traditional lotteries, BitBitWin reveals results instantly, providing immediate
                      gratification and transparency to players.
                    </Text>
                  </Box>
                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      4. Customizable Prize Multiplier
                    </Heading>
                    <Text 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      lineHeight="tall"
                      w="100%"
                    >
                      Players have the freedom to specify their desired prize multiplier. The platform
                      automatically adjusts winning odds based on the selected multiplier.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              <Box 
                p={{ base: 4, md: 6 }} 
                bg="gray.800" 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor="blue.500"
                w="100%"
              >
                <Heading 
                  as="h2" 
                  size={{ base: "md", md: "lg" }} 
                  color="white" 
                  mb={{ base: 2, md: 4 }}
                >
                  Pool Token Economics
                </Heading>
                <VStack align="stretch" spacing={{ base: 3, md: 6 }} w="100%">
                  <TokenomicsCharts />
                  
                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      Profit Distribution
                    </Heading>
                    <VStack 
                      align="stretch" 
                      spacing={{ base: 1, md: 2 }} 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      w="100%"
                    >
                      <Text w="100%">• 70% of funds are allocated to lottery payouts (vs. &lt;40% in traditional lotteries)</Text>
                      <Text w="100%">• 10% is returned to players as lottery rebate in pool tokens</Text>
                      <Text w="100%">• 2% goes to pool maintenance and development</Text>
                      <Text w="100%">• 18% is added to the pool fund to drive token value appreciation</Text>
                      <Text w="100%">• Players who don't win receive pool tokens worth 10% of their bet</Text>
                      <Text w="100%">• The pool fund accumulates value over time, increasing pool token price</Text>
                    </VStack>
                  </Box>

                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      Token Valuation
                    </Heading>
                    <Text 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      lineHeight="tall"
                      w="100%"
                    >
                      Pool token value appreciates naturally over time as the pool fund accumulates. Starting at 1.0x
                      of the underlying asset, the value grows steadily as more players participate. The 18% allocation
                      to the pool fund ensures continuous value backing for the tokens, while the limited distribution
                      rate (12% total) creates natural scarcity and appreciation. Each supported asset has its own
                      independent pool, allowing players to choose their preferred asset exposure.
                    </Text>
                  </Box>

                  <Box w="100%">
                    <Heading 
                      as="h3" 
                      size={{ base: "sm", md: "md" }} 
                      color="blue.400" 
                      mb={{ base: 1, md: 2 }}
                    >
                      Early Adopter Advantage
                    </Heading>
                    <VStack 
                      align="stretch" 
                      spacing={{ base: 1, md: 2 }} 
                      color="gray.400"
                      fontSize={{ base: "xs", md: "md" }}
                      w="100%"
                    >
                      <Text w="100%">• Initial pool token price starts at 1.0x underlying asset</Text>
                      <Text w="100%">• Win Case: 70% of the chance to win direct multiplied payout!</Text>
                      <Text w="100%">• Loss Case: Receive pool tokens worth 10% of bet that appreciate over time</Text>
                      <Text w="100%">• Early participants benefit from maximum token price appreciation</Text>
                      <Text w="100%">• Pool tokens are backed by real assets in the growing pool fund</Text>
                      <Text w="100%">• Choose from multiple asset pools to diversify your participation</Text>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default DocPage;
